import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getAdvisorProStatus,
  type AdvisorProState,
} from '@/api/advisorPro';

const DEFAULT_POLL_INTERVAL_MS = 2000;
const DEFAULT_POLL_TIMEOUT_MS = 60000;

export interface UseAdvisorProStatusOptions {
  /** Skip the initial fetch (e.g. while auth is still resolving). */
  enabled?: boolean;
  /**
   * Keep refetching until `pollUntil` returns true or the timeout elapses.
   * Used on the checkout success screen, where entitlement arrives by webhook
   * rather than with the redirect.
   */
  pollUntil?: (state: AdvisorProState) => boolean;
  pollIntervalMs?: number;
  pollTimeoutMs?: number;
}

export interface UseAdvisorProStatusResult {
  state: AdvisorProState | null;
  loading: boolean;
  error: unknown;
  /** True while polling is still waiting for `pollUntil` to be satisfied. */
  polling: boolean;
  /** True when polling gave up before `pollUntil` was satisfied. */
  pollTimedOut: boolean;
  refetch: () => Promise<AdvisorProState | null>;
}

export function useAdvisorProStatus(
  options: UseAdvisorProStatusOptions = {}
): UseAdvisorProStatusResult {
  const {
    enabled = true,
    pollUntil,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    pollTimeoutMs = DEFAULT_POLL_TIMEOUT_MS,
  } = options;

  const [state, setState] = useState<AdvisorProState | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<unknown>(null);
  const [polling, setPolling] = useState(Boolean(enabled && pollUntil));
  const [pollTimedOut, setPollTimedOut] = useState(false);

  // Keep the predicate in a ref so an inline arrow from the caller does not
  // restart the poll loop on every render.
  const pollUntilRef = useRef(pollUntil);
  pollUntilRef.current = pollUntil;

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchOnce = useCallback(
    async (signal?: AbortSignal): Promise<AdvisorProState | null> => {
      try {
        const response = await getAdvisorProStatus({ signal });
        const next = response.advisor_pro;
        if (mountedRef.current) {
          setState(next);
          setError(null);
        }
        return next;
      } catch (err) {
        if (mountedRef.current) setError(err);
        return null;
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    []
  );

  const refetch = useCallback(() => fetchOnce(), [fetchOnce]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    const deadline = Date.now() + pollTimeoutMs;

    const run = async () => {
      const next = await fetchOnce(controller.signal);
      if (cancelled || !mountedRef.current) return;

      const predicate = pollUntilRef.current;
      if (!predicate) return;

      if (next && predicate(next)) {
        setPolling(false);
        return;
      }

      if (Date.now() >= deadline) {
        setPolling(false);
        setPollTimedOut(true);
        return;
      }

      timer = setTimeout(run, pollIntervalMs);
    };

    void run();

    return () => {
      cancelled = true;
      controller.abort();
      if (timer) clearTimeout(timer);
    };
  }, [enabled, fetchOnce, pollIntervalMs, pollTimeoutMs]);

  return { state, loading, error, polling, pollTimedOut, refetch };
}
